import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";
import { cn } from "@/lib/utils";

/** The design system's pill button: primary (green), secondary (green tint), and ghost (link text). */
const buttonVariants = cva(
  [
    "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-pill border border-transparent",
    "font-sans text-sm leading-[1.4] font-semibold whitespace-nowrap no-underline",
    "transition-colors duration-150 ease-exasol",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring",
    "disabled:cursor-not-allowed",
  ],
  {
    variants: {
      variant: {
        primary:
          "bg-accent text-on-accent hover:bg-accent-hover disabled:border-blue-medium disabled:bg-surface-sunken disabled:text-ink-muted",
        secondary: "border-accent bg-accent-tint text-ink hover:bg-transparent",
        ghost: "bg-transparent text-link hover:underline disabled:text-ink-muted disabled:no-underline",
      },
      size: {
        default: "min-h-10 px-[25px] py-2",
        lg: "min-h-[50px] px-[35px] py-[14px] text-base",
        inline: "min-h-8 px-2 py-1",
      },
    },
    defaultVariants: { variant: "primary", size: "default" },
  },
);

export function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ComponentProps<"button"> & VariantProps<typeof buttonVariants> & { asChild?: boolean }) {
  const Component = asChild ? Slot : "button";
  return <Component data-slot="button" className={cn(buttonVariants({ variant, size }), className)} {...props} />;
}
